import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import BulkSkip from '../shared/bulk_skip';
import { CheckChip, FilterBar, SelectFilter } from '../shared/controls';
import CodeBreaks from '../shared/code_breaks';
import LoadFailed from '../shared/load_failed';
import Pager from '../shared/pager';
import { useStickyBarPadding } from '../shared/use_sticky_bar_padding';
import { last_page_offset } from '../shared/paging';
import StatusPill from '../shared/status_pill';
import { toggle_sort } from '../shared/use_sortable';
import IssueActionModal from './issue_action_modal';
import SessionCell from './session_cell';
import QaOthers from './qa_others';
import MonthPassModal from '../penalties/month_pass_modal';
import { KIND, error_view } from '../penalties/error_handlers';
import { MONTH_PASS_ACTION } from '../penalties/params';
import { month_pass_payload } from '../penalties/requests';
import {
  fetch_issues_csv,
  fetch_needs_attention_issues,
  post_issue_action,
  post_penalty_month_pass
} from '../api';
import { issues_file_name } from '../reports/adapters';
import { error_message, fmt_count, is_permission_error, month_label } from '../format';
import {
  ISSUES_LOAD_FAILED_TEXT,
  ISSUE_ACTION_DROPPED_TEXT,
  close_failure_kind,
  issue_action_view,
  issues_empty_text,
  issues_view
} from './adapters';
import {
  ACTION_ACKNOWLEDGE,
  ACTION_RESOLVE,
  DEFAULT_DIR,
  DEFAULT_SORT,
  DEFAULT_STATUS_LABEL,
  ISSUE_SORT_OPTIONS,
  ISSUE_STATUS_OPTIONS,
  TIER_LABELS,
  applicable_issue_ids,
  issue_action_payload,
  issue_applies,
  detail_lines,
  issue_filter_summary,
  issue_filters_on,
  issues_params,
  offers_month_pass_removal,
  action_verb,
  page_selection_state,
  parse_sort_option,
  refreshed_issue_rows,
  sort_option_value,
  resolve_label,
  toggle_page_selection
} from './params';

/*
 * the issue list of one tier: the Ops label, the suggested action, the raw engine text as detail,
 * and acknowledge / resolve with a mandatory reason. Drilling from a tier row lands here.
 */
const ISSUES_CSV_FAILED_TEXT = 'Could not download the issues CSV. Try again.';
const ISSUES_CSV_EMPTY_TEXT = 'The issues CSV came back empty. Try again.';
const PAGE_SIZE = 100;
const BULK_BAR_ID = 'tp-issues-bulk';

// on_changed re-reads the shell's badges; initial_sort/on_filters carry status and sort to and from the URL.
const IssueTable = ({
  pay_month,
  tier,
  issue_code = '',
  category_label = '',
  back_label = 'Back to tiers',
  teacher_uuid,
  teacher_name = '',
  teacher_filter = null,
  on_clear_teacher,
  initial_status = '',
  initial_sort = null,
  on_filters,
  on_error,
  on_back,
  on_changed
}) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(issues_view(null));
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState(initial_status);
  // {key, dir}, API-driven (server-paginated list): a header click or the Sort select both land here
  // and re-fetch, unlike shared/use_sortable's client-side sort_rows.
  const [sort, setSort] = useState(initial_sort || { key: DEFAULT_SORT, dir: DEFAULT_DIR });
  const [selected, setSelected] = useState([]);
  const [action, setAction] = useState('');
  // a row's own Acknowledge / Resolve button acts on that row alone, whatever is ticked.
  const [row_target, setRowTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [action_result, setActionResult] = useState(null);
  const [load_failed, setLoadFailed] = useState(false);
  const [action_error, setActionError] = useState('');
  const [downloading, setDownloading] = useState(false);
  // a CSV failure shows beside its button, not in the shell toast.
  const [csv_error, setCsvError] = useState('');
  // the rows of an action the connection dropped; the dialog keeps them for the retry.
  const [dropped_rows, setDroppedRows] = useState(null);
  // Counts drops, so a repeat drop with the same text is announced and focused again.
  const [drop_attempt, setDropAttempt] = useState(0);
  // bumped when the dialog closes, so no answer lands in a later dialog.
  const action_run = useRef(0);
  // the row whose Free Pass Day is being removed, and that write's state.
  const [month_pass_row, setMonthPassRow] = useState(null);
  const [month_pass_saving, setMonthPassSaving] = useState(false);
  const [month_pass_error, setMonthPassError] = useState(null);
  // the bulk-bar opener unmounts once the action clears the selection; focus returns here.
  const heading_ref = useRef(null);
  const bar_ref = useRef(null);
  useStickyBarPadding(bar_ref, selected.length > 0);
  const focus_heading = () => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  };

  // Only the latest request may render — a slow tier-A response must never land under tier B.
  const request_seq = useRef(0);

  const load = useCallback(
    (next_offset, next_status, next_sort) => {
      request_seq.current += 1;
      const seq = request_seq.current;
      setLoading(true);
      setLoadFailed(false);
      setSelected([]);
      fetch_needs_attention_issues(
        issues_params(
          {
            pay_month,
            tier,
            issue_code,
            teacher_uuid,
            status: next_status,
            sort: next_sort.key,
            dir: next_sort.dir
          },
          PAGE_SIZE,
          next_offset
        )
      )
        .then((data) => {
          if (seq !== request_seq.current) return;
          const next = issues_view(data);
          const back = last_page_offset({
            offset: next_offset,
            row_count: next.rows.length,
            total_count: next.total_count,
            page_size: PAGE_SIZE
          });
          if (back !== null) {
            setOffset(back);
            load(back, next_status, next_sort);
            return;
          }
          setView(next);
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          on_error(e, 'Failed to load the issues for this tier.');
          setView(issues_view(null));
          setLoadFailed(true);
          setLoading(false);
        });
    },
    [pay_month, tier, issue_code, teacher_uuid, on_error]
  );

  useEffect(() => {
    setOffset(0);
    setCsvError('');
    load(0, status, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month, tier, issue_code, teacher_uuid]);

  const go_page = (next_offset) => {
    setOffset(next_offset);
    load(next_offset, status, sort);
  };

  // Shared by the Sort select and a table header click, so both stay in the same {key, dir} state.
  const apply_sort = (next) => {
    setSort(next);
    setOffset(0);
    load(0, status, next);
    if (on_filters) on_filters({ status, sort: next });
  };

  // the to-be-reviewed CSV is this list under its current filters and sort, every page.
  const download_csv = () => {
    if (downloading) return;
    setDownloading(true);
    setCsvError('');
    fetch_issues_csv({
      pay_month,
      tier,
      issue_code,
      teacher_uuid,
      status,
      sort: sort.key,
      dir: sort.dir
    })
      .then(({ csv, file_name }) => {
        setDownloading(false);
        if (!csv) {
          setCsvError(ISSUES_CSV_EMPTY_TEXT);
          return;
        }
        saveAs(
          new Blob([csv], { type: 'text/csv;charset=utf-8' }),
          file_name || issues_file_name(pay_month)
        );
      })
      .catch((e) => {
        setDownloading(false);
        // A lost grant is the shell's no-permission page; anything else stays on this button.
        if (is_permission_error(e)) on_error(e, ISSUES_CSV_FAILED_TEXT);
        else setCsvError(ISSUES_CSV_FAILED_TEXT);
      });
  };

  const toggle = (id) =>
    setSelected((s) => (s.indexOf(id) === -1 ? s.concat([id]) : s.filter((x) => x !== id)));
  const page_pick = page_selection_state(selected, view.rows);
  const page_box = useRef(null);
  useLayoutEffect(() => {
    if (page_box.current) page_box.current.indeterminate = page_pick.some;
  }, [page_pick.some, loading]);

  const dialog_rows = dropped_rows
    ? refreshed_issue_rows(dropped_rows, view.rows)
    : row_target
      ? refreshed_issue_rows(row_target, view.rows)
      : view.rows.filter((r) => selected.indexOf(r.id) !== -1);

  const open_row_action = (next_action, row) => {
    setRowTarget([row]);
    setAction(next_action);
  };

  const open_bulk_action = (next_action) => {
    setRowTarget(null);
    setAction(next_action);
  };

  const submit_action = (reason) => {
    const sent_rows = dialog_rows;
    const ids = applicable_issue_ids(
      sent_rows,
      sent_rows.map((r) => r.id),
      action
    );
    if (!ids.length) return;
    const run = action_run.current;
    setSubmitting(true);
    setActionError('');
    post_issue_action(issue_action_payload(ids, action, reason))
      .then((data) => {
        if (run !== action_run.current) return;
        setDroppedRows(null);
        const acted = sent_rows.filter((r) => ids.indexOf(Number(r.id)) !== -1);
        setActionResult(issue_action_view(data, action_verb(action, acted), acted));
        setSubmitting(false);
        load(offset, status, sort);
        if (on_changed) on_changed();
      })
      .catch((e) => {
        if (run !== action_run.current) return;
        setSubmitting(false);
        const kind = close_failure_kind(e);
        if (kind === 'reason_too_long') {
          setActionError(error_message(e, 'The reason is too long.'));
          return;
        }
        if (kind === 'dropped') {
          // in the dialog, not the 5xx body: that would hide that the action may have landed.
          setDroppedRows(sent_rows);
          setDropAttempt((n) => n + 1);
          load(offset, status, sort);
          if (on_changed) on_changed();
          return;
        }
        on_error(e, 'Failed to save the issue action.');
      });
  };

  const close_month_pass = () => {
    setMonthPassRow(null);
    setMonthPassError(null);
  };

  // The removal recomputes the month, which clears this issue; the list and the shell's badges re-read.
  const submit_month_pass = (note) => {
    if (month_pass_saving || !month_pass_row) return;
    setMonthPassSaving(true);
    setMonthPassError(null);
    const row = month_pass_row;
    post_penalty_month_pass(
      month_pass_payload(row.teacher_uuid, row.pay_month, MONTH_PASS_ACTION.REMOVE, note)
    )
      .then(() => {
        close_month_pass();
        load(offset, status, sort);
        if (on_changed) on_changed();
      })
      .catch((e) => {
        const v = error_view(e, { write: true });
        if (v.kind === KIND.PERMISSION) {
          close_month_pass();
          on_error(e, 'Failed to remove the Free Pass Day.');
          return;
        }
        setMonthPassError(v);
      })
      .finally(() => setMonthPassSaving(false));
  };

  const close_modal = () => {
    action_run.current += 1;
    setSubmitting(false);
    setAction('');
    setRowTarget(null);
    setActionResult(null);
    setActionError('');
    setDroppedRows(null);
  };

  // Kuldeep: a list of ONE category says its label and suggested action once, in the panel header;
  // its rows lead with what differs per row (teacher, engine detail).
  const one_category = !!issue_code;
  const category_action = one_category
    ? (view.rows.find((r) => r.suggested_action) || {}).suggested_action || ''
    : '';

  const has_text = (v) => !!v && v !== '—';
  const free_pass_link = (r) =>
    offers_month_pass_removal(r) ? (
      <button
        type="button"
        className="tp-link"
        aria-label={`Remove ${r.teacher_name}'s Free Pass Day for ${month_label(r.pay_month)}`}
        onClick={() => setMonthPassRow(r)}
      >
        Remove Free Pass Day
      </button>
    ) : null;

  // The issue code (upper case as sent, mono) and the engine's reason under it, in full.
  const code_and_reason = (r) => (
    <>
      <div>
        <span className="tp-mono tp-na-code">
          <CodeBreaks text={r.issue_code} />
        </span>
      </div>
      {r.flag_reason ? <div className="tp-na-reason">{r.flag_reason}</div> : null}
    </>
  );

  // What: one category leads with the code (its label is the list header); a mixed list leads
  // with the Ops label, its tier and suggested action, then the code.
  const issue_cell = (r) =>
    one_category ? (
      <div className="tp-c-what">
        {code_and_reason(r)}
        {detail_lines(r.issue_code, r.detail).map((line) => (
          <div key={line} className="tp-c-line tp-na-detail">
            {line}
          </div>
        ))}
        {r.app_join_text ? <div className="tp-c-line tp-na-detail">{r.app_join_text}</div> : null}
        {r.qa_sticky_text ? <div className="tp-c-line tp-sub">{r.qa_sticky_text}</div> : null}
        <QaOthers text={r.qa_others_text} />
        {free_pass_link(r)}
      </div>
    ) : (
      <What title={r.label}>
        {!tier && !issue_code ? (
          <div>
            <StatusPill tone={r.tier_tone}>{r.tier_label}</StatusPill>
          </div>
        ) : null}
        {r.suggested_action ? <div className="tp-c-line tp-sub">{r.suggested_action}</div> : null}
        {code_and_reason(r)}
        {detail_lines(r.issue_code, r.detail).map((line) => (
          <div key={line} className="tp-c-line tp-na-detail">
            {line}
          </div>
        ))}
        {r.app_join_text ? <div className="tp-c-line tp-na-detail">{r.app_join_text}</div> : null}
        {r.qa_sticky_text ? <div className="tp-c-line tp-sub">{r.qa_sticky_text}</div> : null}
        <QaOthers text={r.qa_others_text} />
        {free_pass_link(r)}
      </What>
    );

  const row_buttons = (r, wrap) => {
    const can_ack = issue_applies(r, ACTION_ACKNOWLEDGE);
    const can_resolve = issue_applies(r, ACTION_RESOLVE);
    if (!can_ack && !can_resolve) return null;
    return (
      <div className={wrap}>
        {can_ack ? (
          <button
            type="button"
            className="tp-btn-out tp-na-act"
            aria-label={`Acknowledge issue ${r.id} for ${r.teacher_name}`}
            onClick={() => open_row_action(ACTION_ACKNOWLEDGE, r)}
          >
            Acknowledge
          </button>
        ) : null}
        {can_resolve ? (
          <button
            type="button"
            className="tp-btn-out tp-na-act"
            aria-label={`${resolve_label(r.issue_code)} issue ${r.id} for ${r.teacher_name}`}
            onClick={() => open_row_action(ACTION_RESOLVE, r)}
          >
            {resolve_label(r.issue_code)}
          </button>
        ) : null}
      </div>
    );
  };
  const status_chip = (r) => <StatusPill tone={r.status_tone}>{r.status_label}</StatusPill>;

  // Four tracks (list-row pattern): teacher · issue · ₹ at stake · status and actions; the Ops
  // decision runs under the row as a note. Nothing folds; below 660px each issue is a card.
  const columns = [
    {
      key: 'select',
      role: 'select',
      label: <span className="sr-only">Select</span>,
      track: '34px',
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.indexOf(r.id) !== -1}
          onChange={() => toggle(r.id)}
          aria-label={`Select issue ${r.id}`}
          disabled={!r.selectable}
        />
      )
    },
    {
      key: 'teacher_name',
      role: 'identifier',
      label: 'Teacher',
      sort_key: 'teacher_name',
      track: { compact: '28%', full: '26%' },
      render: (r) => (
        <Who
          name={r.teacher_name}
          sub={
            has_text(r.session_start_text) ? (
              <div className="tp-na-sess">
                <SessionCell row={r} />
              </div>
            ) : null
          }
          meta={[
            has_text(r.session_status_text) ? `Session ${r.session_status_text}` : '',
            'Found by system',
            `First ${r.first_seen}`,
            `Last ${r.last_seen}`,
            r.teacher_slug
          ]}
        />
      )
    },
    {
      key: 'label',
      label: one_category ? 'Engine detail' : 'Issue',
      sort_key: 'issue',
      render: issue_cell
    },
    {
      key: 'amount_at_stake',
      role: 'amount',
      label: '₹ at stake',
      align: 'right',
      sort_key: 'amount',
      track: { compact: '136px', full: '150px' },
      render: (r) => (
        <div className="tp-na-stake">
          <Ledger total={{ label: 'At stake', value: r.amount_text }} />
        </div>
      )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      align: 'right',
      sort_key: 'status',
      track: { compact: '148px', full: '172px' },
      render: (r) => (
        <StatusCell chip={status_chip(r)} actions={row_buttons(r, 'tp-c-acts tp-na-acts')} />
      ),
      card_render: status_chip
    },
    {
      key: 'resolution',
      label: 'Decision',
      line: { from: 'label', bare: true },
      render: (r) =>
        r.resolution_text || r.resolution_note ? (
          <Note
            label={
              r.resolution_text ? (
                <span title={r.resolver_uuid || undefined}>{r.resolution_text}</span>
              ) : null
            }
            text={r.resolution_note}
          />
        ) : null
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Action',
      in_table: false,
      card_render: (r) => row_buttons(r, 'tp-row-actions')
    }
  ];

  const page_start = view.total_count === 0 ? 0 : offset + 1;
  const page_end = Math.min(offset + view.rows.length, view.total_count);

  const set_status = (next) => {
    setStatus(next);
    setOffset(0);
    load(0, next, sort);
    if (on_filters) on_filters({ status: next, sort });
  };
  // The teacher belongs to the shell (clearing it returns to the tiers); status is this list's own.
  const clear_filters = () => {
    if (teacher_uuid && on_clear_teacher) {
      on_clear_teacher();
      return;
    }
    set_status('');
  };
  const teacher_row = teacher_uuid ? view.rows.find((r) => r.teacher_uuid === teacher_uuid) : null;
  // A category opened from the URL has no label yet; its rows carry it.
  const code_row =
    issue_code && !category_label ? view.rows.find((r) => r.issue_code === issue_code) : null;
  const scope_title = category_label || (code_row ? code_row.label : '');
  const summary = load_failed
    ? null
    : issue_filter_summary({
        total: view.total_count,
        month_label: month_label(pay_month),
        scope_label: scope_title || TIER_LABELS[tier] || '',
        status,
        teacher_uuid,
        teacher_name: teacher_name || (teacher_row ? teacher_row.teacher_name : ''),
        loading
      });

  return (
    <div className="tp-na-issues">
      <FilterBar
        label="Issue filters"
        summary={
          summary ? (
            <>
              Showing <b>{summary.lead}</b>
              {summary.rest}
            </>
          ) : null
        }
      >
        <button type="button" className="tp-btn-out tp-na-back" onClick={on_back}>
          ← {back_label}
        </button>
        {teacher_filter}
        <SelectFilter
          id="tp-na-status"
          label="Status"
          aria_label="Issue status filter"
          value={status}
          all_label={DEFAULT_STATUS_LABEL}
          options={ISSUE_STATUS_OPTIONS}
          onChange={set_status}
        />
        {issue_filters_on({ status, teacher_uuid }) ? (
          <button type="button" className="tp-link tp-fbar-end" onClick={clear_filters}>
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      <div className="tp-card">
        <div className="tp-card-hd tp-na-list-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title" tabIndex={-1} ref={heading_ref}>
              {scope_title || TIER_LABELS[tier] || 'All issues'}
            </span>
            {loading || load_failed ? null : (
              <span className="tp-card-hd-sub">
                {view.total_count === 0
                  ? 'No issues'
                  : `Showing ${page_start}–${page_end} of ${view.total_count} · ${view.amount_text} at stake`}
                {category_action ? (
                  <span className="tp-na-cat-action">{category_action}</span>
                ) : null}
              </span>
            )}
          </span>
          <span className="tp-card-hd-acts">
            {!loading && !load_failed && page_pick.count ? (
              <CheckChip
                className="tp-na-pick-all"
                inputRef={page_box}
                checked={page_pick.all}
                onChange={() => setSelected((s) => toggle_page_selection(s, view.rows))}
              >
                Select all on page ({fmt_count(page_pick.count)})
              </CheckChip>
            ) : null}
            <SelectFilter
              id="tp-na-sort"
              label="Sort"
              aria_label="Sort issues by"
              value={sort_option_value(sort)}
              all_label="Tier, then ₹ at stake"
              options={ISSUE_SORT_OPTIONS}
              active={false}
              onChange={(v) => apply_sort(parse_sort_option(v))}
            />
            {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares. */}
            <button
              type="button"
              className="tp-btn-out tp-na-csv"
              onClick={download_csv}
              aria-disabled={downloading ? 'true' : undefined}
            >
              {downloading ? 'Preparing…' : '↓ Download CSV'}
            </button>
          </span>
          {csv_error ? (
            <span className="tp-card-hd-note tp-rp-card-error tp-na-csv-error" role="alert">
              {csv_error}
            </span>
          ) : null}
        </div>
        <BulkSkip count={selected.length} target_id={BULK_BAR_ID} />
        {loading ? <CustomLoading /> : null}
        {!loading && load_failed ? (
          <LoadFailed text={ISSUES_LOAD_FAILED_TEXT} on_retry={() => load(offset, status, sort)} />
        ) : null}
        {!loading && !load_failed ? (
          <DataTable
            columns={columns}
            rows={view.rows}
            rowKey={(r) => r.key}
            sort={sort}
            on_sort={(key) => apply_sort(toggle_sort(sort, key))}
            rowClassName={(r) => (selected.indexOf(r.id) !== -1 ? 'tp-row-sel' : '')}
            emptyText={issues_empty_text(status)}
            card_below={660}
            responsive
          />
        ) : null}
      </div>

      {!load_failed ? (
        <Pager
          offset={offset}
          rows={view.rows.length}
          total={view.total_count}
          page_size={PAGE_SIZE}
          on_page={go_page}
        />
      ) : null}

      {/* After the list and pager: sticky at the viewport bottom while rows are selected, and never
          covering the pager or the last row once scrolled to the end. */}
      {selected.length ? (
        <div
          ref={bar_ref}
          id={BULK_BAR_ID}
          tabIndex={-1}
          className="tp-bulkbar tp-bulkbar-sticky"
          role="region"
          aria-label="Selected issues"
        >
          <span className="tp-bulkbar-sum">
            <b>{fmt_count(selected.length)} selected</b>
          </span>
          <button
            type="button"
            className="tp-btn"
            onClick={() => open_bulk_action(ACTION_ACKNOWLEDGE)}
            disabled={!applicable_issue_ids(view.rows, selected, ACTION_ACKNOWLEDGE).length}
          >
            Acknowledge selected
          </button>
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => open_bulk_action(ACTION_RESOLVE)}
            disabled={!applicable_issue_ids(view.rows, selected, ACTION_RESOLVE).length}
          >
            Resolve selected
          </button>
          <button type="button" className="tp-link" onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      ) : null}

      {month_pass_row ? (
        <MonthPassModal
          action={MONTH_PASS_ACTION.REMOVE}
          teacher_name={month_pass_row.teacher_name}
          month_text={month_label(month_pass_row.pay_month)}
          saving={month_pass_saving}
          error={month_pass_error}
          on_submit={submit_month_pass}
          on_close={close_month_pass}
          on_focus_lost={focus_heading}
        />
      ) : null}

      <IssueActionModal
        show={!!action}
        action={action}
        rows={dialog_rows}
        submitting={submitting}
        result={action_result}
        dropped_text={dropped_rows ? ISSUE_ACTION_DROPPED_TEXT : ''}
        dropped_attempt={drop_attempt}
        server_error={action_error}
        on_focus_lost={focus_heading}
        on_close={close_modal}
        on_submit={submit_action}
      />
    </div>
  );
};

export default IssueTable;
